import { PopoverPlacement } from "@idui/react-popover";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  currentUserIdSelector,
  IRideDto,
  isTodayDate,
  shouldShowUserUpdaterIndicator,
  useFormats,
  UserEditDisplayModeEnum,
  useTimezone,
} from "@ryde/shared";

import {
  FramerTooltip,
  getUserEditMode,
  getUserUpdaterIcon,
  UserIconReplacement,
} from "../../../../common";
import { UserInfoContainerStyled } from "./summary.rides.styled";

interface IUserUpdaterIndicatorRides {
  rideDto: Partial<IRideDto>;
  displayTimeLabel?: boolean;
}

export const UserUpdaterIndicatorRides: FC<IUserUpdaterIndicatorRides> = ({
  rideDto,
  displayTimeLabel,
}) => {
  const { getFormattedEditUserInfo, formatTimeHHmmNormalized } = useFormats();
  const { updatedUserName, updatedUserId, updatedAt, metadata } = rideDto;
  const { timezone } = useTimezone();
  const currentUserId = useRecoilValue(currentUserIdSelector);
  const { hasSelfRemovedPassengers } = metadata ?? {};
  const { t } = useTranslation();

  const toolTipContent = useMemo(() => {
    if (hasSelfRemovedPassengers) {
      return t(
        `selectListLabel.${UserEditDisplayModeEnum.PassengerSelfRemoved}`,
      );
    }

    const lastUpdater = getFormattedEditUserInfo(
      updatedUserName ? updatedUserName : `${updatedUserId}`,
      updatedAt,
    );
    return lastUpdater;
  }, [
    getFormattedEditUserInfo,
    hasSelfRemovedPassengers,
    updatedAt,
    updatedUserId,
    updatedUserName,
    t,
  ]);

  const isToday = useMemo(() => {
    return isTodayDate(updatedAt, timezone);
  }, [timezone, updatedAt]);

  const useEditModeIcon = useMemo(() => {
    if (hasSelfRemovedPassengers) {
      return getUserUpdaterIcon(UserEditDisplayModeEnum.PassengerSelfRemoved);
    }

    if (!updatedUserId) {
      return <UserIconReplacement />;
    }

    const isCurrentUser = updatedUserId === currentUserId;
    const mode = getUserEditMode(isToday, isCurrentUser);

    return getUserUpdaterIcon(mode);
  }, [currentUserId, isToday, hasSelfRemovedPassengers, updatedUserId]);

  return (
    shouldShowUserUpdaterIndicator(rideDto) && (
      <FramerTooltip
        content={toolTipContent}
        placement={PopoverPlacement.bottom}
      >
        <UserInfoContainerStyled $isToday={isToday}>
          {!!displayTimeLabel && !!updatedAt && (
            <span className="time_label">
              {formatTimeHHmmNormalized(updatedAt)}
            </span>
          )}
          {useEditModeIcon}
        </UserInfoContainerStyled>
      </FramerTooltip>
    )
  );
};
